import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { trackQuizCompleted } from '@/lib/analytics';
import { useCurrency } from '@/context/CurrencyContext';
import { ArrowRight, ArrowLeft, PersonSimpleRun, Barbell, TShirt, Basketball, PersonSimpleWalk, Mountains, CircleNotch, Palette, Check, Boot, HighHeel, Baby, Briefcase, SunHorizon, Ruler, GraduationCap, SoccerBall, Sneaker, ArrowsHorizontal, X } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const brandOptions = ['Adidas', 'Allen Edmonds', 'ASICS', 'Birkenstock', 'Blundstone', 'Bobux', 'Brooks', 'Canterbury', 'Church\'s', 'Clarks', 'Cole Haan', 'Converse', 'Cosyfeet', 'Crocs', 'Diadora', 'Dr. Martens', 'Durango', 'Esska', 'Georgia Boot', 'HOKA', 'Jordan', 'KEEN', 'Loake', 'Merrell', 'Mizuno', 'New Balance', 'Nike', 'On', 'Puma', 'Reebok', 'Rocky', 'Salomon', 'Saucony', 'Skechers', 'Steve Madden', 'Stride Rite', 'Timberland', 'UGG', 'Under Armour', 'Vans'];

const colorOptions = [
  { value: 'black', label: 'Black', hex: '#0A0A0A' },
  { value: 'white', label: 'White', hex: '#FFFFFF' },
  { value: 'grey', label: 'Grey', hex: '#9CA3AF' },
  { value: 'red', label: 'Red', hex: '#EF4444' },
  { value: 'blue', label: 'Blue', hex: '#3B82F6' },
  { value: 'green', label: 'Green', hex: '#22C55E' },
  { value: 'yellow', label: 'Yellow', hex: '#EAB308' },
  { value: 'orange', label: 'Orange', hex: '#F97316' },
  { value: 'pink', label: 'Pink', hex: '#EC4899' },
  { value: 'purple', label: 'Purple', hex: '#A855F7' },
  { value: 'brown', label: 'Brown', hex: '#92400E' },
  { value: 'navy', label: 'Navy', hex: '#1E3A5F' },
];

const sizeChart = [
  { us: '4',   uk: '3',   eu: '36',  cm: '22.0' },
  { us: '4.5', uk: '3.5', eu: '36.5', cm: '22.5' },
  { us: '5',   uk: '4',   eu: '37',  cm: '23.0' },
  { us: '5.5', uk: '4.5', eu: '37.5', cm: '23.5' },
  { us: '6',   uk: '5',   eu: '38',  cm: '24.0' },
  { us: '6.5', uk: '5.5', eu: '38.5', cm: '24.5' },
  { us: '7',   uk: '6',   eu: '39',  cm: '25.0' },
  { us: '7.5', uk: '6.5', eu: '40',  cm: '25.5' },
  { us: '8',   uk: '7',   eu: '40.5', cm: '26.0' },
  { us: '8.5', uk: '7.5', eu: '41',  cm: '26.5' },
  { us: '9',   uk: '8',   eu: '42',  cm: '27.0' },
  { us: '9.5', uk: '8.5', eu: '42.5', cm: '27.5' },
  { us: '10',  uk: '9',   eu: '43',  cm: '28.0' },
  { us: '10.5',uk: '9.5', eu: '44',  cm: '28.5' },
  { us: '11',  uk: '10',  eu: '44.5', cm: '29.0' },
  { us: '11.5',uk: '10.5',eu: '45',  cm: '29.5' },
  { us: '12',  uk: '11',  eu: '46',  cm: '30.0' },
  { us: '12.5',uk: '11.5',eu: '46.5', cm: '30.5' },
  { us: '13',  uk: '12',  eu: '47',  cm: '31.0' },
  { us: '14',  uk: '13',  eu: '48',  cm: '32.0' },
];

function cmToUsSize(cm) {
  const val = parseFloat(cm);
  if (isNaN(val) || val < 20 || val > 35) return null;
  let closest = sizeChart[0];
  let minDiff = 999;
  for (const row of sizeChart) {
    const diff = Math.abs(parseFloat(row.cm) - val);
    if (diff < minDiff) { minDiff = diff; closest = row; }
  }
  return closest;
}

/**
 * Determine width fitting from foot length and width measurements.
 * Uses standard shoe width tables: D width at a given foot length has a
 * specific standard width (cm). Each width code is ~6mm apart.
 *
 * Base: US Men's 9 (27cm length) → D width = 9.7cm
 * Each half-size (0.5cm length) adds ~1.5mm to the standard width.
 */
function measureWidth(lengthCm, widthCm) {
  const len = parseFloat(lengthCm);
  const wid = parseFloat(widthCm);
  if (isNaN(len) || isNaN(wid) || len < 20 || len > 35 || wid < 5 || wid > 15) return null;

  // Standard D-width at 27cm foot length = 9.7cm
  // Adjust for foot length: +1.5mm per 0.5cm of length above 27cm
  const lengthDelta = (len - 27.0) / 0.5;
  const standardD = 9.7 + (lengthDelta * 0.15);

  const diff = wid - standardD;

  // Each width code is ~6mm apart
  if (diff < -0.9) return { code: '2A', label: '2A (AA)', desc: 'Narrow' };
  if (diff < -0.3) return { code: 'B', label: 'B', desc: 'Narrow (Men) / Standard (Women)' };
  if (diff < 0.3)  return { code: 'D', label: 'D', desc: 'Standard (Men) / Wide (Women)' };
  if (diff < 0.9)  return { code: 'E', label: 'E / 2E', desc: 'Wide' };
  return { code: '3E', label: '3E / 4E', desc: 'Extra Wide' };
}

const widthOptions = [
  { value: '2A', label: '2A (AA)', desc: 'Narrow' },
  { value: 'B', label: 'B', desc: 'Narrow (Men) / Standard (Women)' },
  { value: 'D', label: 'D', desc: 'Standard (Men) / Wide (Women)' },
  { value: 'E', label: 'E / 2E', desc: 'Wide' },
  { value: '3E', label: '3E / 4E', desc: 'Extra Wide' },
];

/*
 * QUIZ STEP ORDER:
 * 1. Gender
 * 2. Activity (shoe type)
 * 3. Preferred Brands
 * 4. Preferred Colours
 * 5. Budget
 * 6. Shoe Size (with optional foot length+width measurement)
 * 7. Width Fitting (with AI recommendation from measurement, or manual pick)
 * 8. Arch Type
 */
const steps = [
  { key: 'gender', title: 'WHO ARE YOU SHOPPING FOR?',
    options: [
      { value: 'mens', label: "Men's" },
      { value: 'womens', label: "Women's" },
      { value: 'unisex', label: 'Unisex' },
    ]
  },
  { key: 'activity', title: 'WHAT TYPE OF SHOE?', dynamicOptions: true },
  { key: 'preferred_brands', title: 'PREFERRED BRANDS?',
    subtitle: 'Select one or more (or skip for no preference)', multiSelect: true },
  { key: 'budget', title: 'BUDGET RANGE?', skippable: true,
    options: [
      { value: '50-100', label: '$50 - $100' },
      { value: '100-150', label: '$100 - $150' },
      { value: '150-200', label: '$150 - $200' },
      { value: '200-300', label: '$200+' },
    ]
  },
  { key: 'size', title: 'YOUR SHOE SIZE?', isCustomSize: true },
  { key: 'foot_width', title: 'WIDTH FITTING?',
    subtitle: 'Based on your measurements, or choose manually',
    isWidthStep: true },
  { key: 'arch_type', title: 'ARCH TYPE?',
    options: [
      { value: 'low', label: 'Low / Flat' },
      { value: 'normal', label: 'Normal' },
      { value: 'high', label: 'High' },
    ]
  },
];

const allActivityOptions = [
  { value: 'running', label: 'Running', icon: <PersonSimpleRun size={32} weight="duotone" />, for: ['mens', 'womens', 'unisex'] },
  { value: 'training', label: 'Training', icon: <Barbell size={32} weight="duotone" />, for: ['mens', 'womens', 'unisex'] },
  { value: 'casual', label: 'Casual / Everyday', icon: <TShirt size={32} weight="duotone" />, for: ['mens', 'womens', 'unisex'] },
  { value: 'basketball', label: 'Basketball', icon: <Basketball size={32} weight="duotone" />, for: ['mens', 'unisex'] },
  { value: 'football', label: 'Football / Rugby / Soccer', icon: <SoccerBall size={32} weight="duotone" />, for: ['mens', 'womens', 'unisex'] },
  { value: 'walking', label: 'Walking', icon: <PersonSimpleWalk size={32} weight="duotone" />, for: ['mens', 'womens', 'unisex'] },
  { value: 'hiking', label: 'Hiking / Outdoor', icon: <Mountains size={32} weight="duotone" />, for: ['mens', 'womens', 'unisex'] },
  { value: 'boots', label: 'Boots', icon: <Boot size={32} weight="duotone" />, for: ['mens', 'womens', 'unisex'] },
  { value: 'fashion', label: "Women's Fashion", icon: <HighHeel size={32} weight="duotone" />, for: ['womens'] },
  { value: 'formal', label: "Men's Formal / Dress", icon: <Briefcase size={32} weight="duotone" />, for: ['mens'] },
  { value: 'sandals', label: "Sandals & Flip Flops", icon: <SunHorizon size={32} weight="duotone" />, for: ['mens', 'womens', 'unisex'] },
  { value: 'school', label: "School Shoes", icon: <GraduationCap size={32} weight="duotone" />, for: ['mens', 'womens', 'unisex'] },
  { value: 'kids', label: "Kids / Children's", icon: <Baby size={32} weight="duotone" />, for: ['mens', 'womens', 'unisex'] },
];

function getActivityOptions(gender) {
  if (!gender) return allActivityOptions;
  return allActivityOptions.filter(opt => opt.for.includes(gender));
}

export default function QuizPage() {
  const location = useLocation();
  const prefill = location.state?.prefill || null;
  const jumpToStep = location.state?.jumpToStep || null;
  const initialStep = jumpToStep
    ? Math.max(0, steps.findIndex(s => s.key === jumpToStep))
    : 0;
  const [step, setStep] = useState(initialStep);
  const [answers, setAnswers] = useState(() => ({
    preferred_brands: [],
    preferred_colors: [],
    ...(prefill || {}),
  }));
  const [loading, setLoading] = useState(false);
  const [sizeMode, setSizeMode] = useState('chart');
  const [footCm, setFootCm] = useState('');
  const [footWidthCm, setFootWidthCm] = useState('');
  const [showFullChart, setShowFullChart] = useState(false);
  const navigate = useNavigate();
  const { region } = useCurrency();
  const current = steps[step];
  const progress = ((step + 1) / steps.length) * 100;

  const currentOptions = current.dynamicOptions
    ? getActivityOptions(answers.gender)
    : current.options;

  // Computed measurements
  const measuredSize = footCm ? cmToUsSize(footCm) : null;
  const measuredWidth = (footCm && footWidthCm) ? measureWidth(footCm, footWidthCm) : null;

  const selectOption = async (value) => {
    const newAnswers = { ...answers, [current.key]: value };
    setAnswers(newAnswers);
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      submitQuiz(newAnswers);
    }
  };

  const toggleMultiSelect = (key, value) => {
    const current_vals = answers[key] || [];
    if (current_vals.includes(value)) {
      setAnswers({ ...answers, [key]: current_vals.filter(v => v !== value) });
    } else {
      setAnswers({ ...answers, [key]: [...current_vals, value] });
    }
  };

  const skipOrContinueMulti = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      submitQuiz(answers);
    }
  };

  const [error, setError] = useState('');
  const [lastSubmission, setLastSubmission] = useState(null);

  const submitQuiz = async (finalAnswers, attempt = 1) => {
    setLoading(true);
    setError('');
    setLastSubmission(finalAnswers);

    // Use fetch + AbortController — more reliable than axios on iOS Safari
    // (axios sometimes loses XHRs when tab is backgrounded on cellular).
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000); // 20s ceiling

    // On retries 2 and 3, drop credentials. iOS Safari ITP can silently block
    // cross-origin credentialed fetches (`solefinder.app` → backend host).
    // The endpoint works fine anonymously; logged-in users just lose the
    // server-side profile-save side-effect on those attempts.
    const useCredentials = attempt === 1;

    try {
      const res = await fetch(`${API}/quiz/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...finalAnswers, region: finalAnswers.region || region }),
        credentials: useCredentials ? 'include' : 'omit',
        signal: controller.signal,
        cache: 'no-store',
        keepalive: true,
      });
      clearTimeout(timeoutId);

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`Server ${res.status}: ${txt.slice(0, 120) || 'request failed'}`);
      }

      const data = await res.json();
      if (!data?.profile_id) {
        throw new Error('Invalid response from server');
      }
      // GA: track successful quiz completion with key segmentation params
      trackQuizCompleted({
        region: finalAnswers.region,
        activity: finalAnswers.activity,
        footWidth: finalAnswers.foot_width,
        archType: finalAnswers.arch_type,
        budget: finalAnswers.budget,
        gender: finalAnswers.gender,
        recCount: data.recommendations?.length,
      });
      navigate(`/results/${data.profile_id}`, { state: { results: data } });
    } catch (e) {
      clearTimeout(timeoutId);
      console.error(`Quiz submit error (attempt ${attempt}, credentials=${useCredentials}):`, e);

      const isAbort = e.name === 'AbortError';
      const isNetwork = isAbort || e instanceof TypeError ||
                        (typeof e.message === 'string' && /Failed to fetch|Network|timeout|Load failed/i.test(e.message));

      // Retry up to 2 times on network errors with short backoff
      if (isNetwork && attempt < 3) {
        await new Promise(r => setTimeout(r, 600 * attempt));
        return submitQuiz(finalAnswers, attempt + 1);
      }

      setLoading(false);
      setError(
        isAbort
          ? 'The request took too long. Please check your connection and try again.'
          : (e.message || 'Could not analyze your fit. Check your connection and try again.')
      );
    }
  };

  const retrySubmission = () => {
    if (lastSubmission) submitQuiz(lastSubmission);
  };

  const confirmMeasuredSize = () => {
    if (measuredSize) {
      // Store the foot measurements in answers for the width step
      const newAnswers = {
        ...answers,
        size: measuredSize.us,
        _footLengthCm: footCm,
        _footWidthCm: footWidthCm,
      };
      setAnswers(newAnswers);
      setStep(step + 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-6 px-6" data-testid="quiz-loading">
        <CircleNotch size={56} className="animate-spin text-[#FF3B30]" />
        <div className="text-center max-w-md">
          <h2 className="font-heading font-bold text-2xl tracking-tight uppercase">Analyzing Your Fit</h2>
          <p className="text-[#52525B] mt-2">Our AI is matching you with the perfect shoes from 944+ options across 34 retailers.</p>
          <p className="text-[11px] text-[#A1A1AA] mt-4 uppercase tracking-wide">This usually takes 5–15 seconds</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="quiz-page">
      {/* Progress + Exit */}
      <div className="relative">
        <div className="w-full h-1 bg-[#E4E4E7]">
          <motion.div className="h-full bg-[#FF3B30]" animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
        </div>
        <Link to="/" className="absolute top-4 right-6 flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#FF3B30] transition-colors" data-testid="quiz-exit-btn">
          <X size={18} /> Exit
        </Link>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 pb-32 sm:pb-12 max-w-4xl mx-auto w-full">
        {error && (
          <div className="w-full max-w-md mb-6 bg-[#FFF1F0] border-2 border-[#FF3B30] p-4 text-center" data-testid="quiz-error">
            <p className="text-sm font-bold text-[#0A0A0A] uppercase tracking-wide mb-1">Something went wrong</p>
            <p className="text-sm text-[#52525B] mb-3">{error}</p>
            {lastSubmission && (
              <button
                onClick={retrySubmission}
                className="inline-flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-6 py-2.5 text-xs hover:bg-[#D62828] transition-colors"
                data-testid="quiz-retry-btn">
                Try Again <ArrowRight size={14} />
              </button>
            )}
          </div>
        )}
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#52525B] mb-2" data-testid="quiz-step-label">Step {step + 1} of {steps.length}</p>

        <AnimatePresence mode="wait">
          <motion.div key={step} className="w-full" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.25 }}>
            <h2 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl tracking-tighter uppercase text-center mb-2" data-testid="quiz-question">{current.title}</h2>
            {current.subtitle && <p className="text-center text-sm text-[#52525B] mb-8">{current.subtitle}</p>}
            {!current.subtitle && !current.isCustomSize && !current.isWidthStep && <div className="mb-8" />}

            {/* Multi-select: Brand preference */}
            {current.key === 'preferred_brands' && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {brandOptions.map(brand => {
                    const selected = (answers.preferred_brands || []).includes(brand);
                    return (
                      <button key={brand} onClick={() => toggleMultiSelect('preferred_brands', brand)}
                        data-testid={`quiz-brand-${brand.toLowerCase().replace(/\s/g, '-')}`}
                        className={`border-2 bg-white p-4 hover:border-[#0A0A0A] cursor-pointer transition-all flex items-center justify-center gap-2 text-center
                          ${selected ? 'border-[#FF3B30] bg-red-50' : 'border-[#E4E4E7]'}`}>
                        {selected && <Check size={16} className="text-[#FF3B30]" />}
                        <span className="font-bold text-sm uppercase tracking-wide">{brand}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex justify-center gap-4 mt-8">
                  <button onClick={skipOrContinueMulti}
                    className="inline-flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#D62828] transition-colors"
                    data-testid="quiz-brands-continue">
                    {(answers.preferred_brands || []).length > 0 ? 'Continue' : 'Skip — No Preference'} <ArrowRight size={18} />
                  </button>
                </div>
              </>
            )}

            {/* Multi-select: Color preference */}
            {current.key === 'preferred_colors' && (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                  {colorOptions.map(color => {
                    const selected = (answers.preferred_colors || []).includes(color.value);
                    return (
                      <button key={color.value} onClick={() => toggleMultiSelect('preferred_colors', color.value)}
                        data-testid={`quiz-color-${color.value}`}
                        className={`border-2 bg-white p-4 hover:border-[#0A0A0A] cursor-pointer transition-all flex flex-col items-center gap-2 text-center
                          ${selected ? 'border-[#FF3B30] bg-red-50' : 'border-[#E4E4E7]'}`}>
                        <div className="w-8 h-8 rounded-full border border-[#E4E4E7]" style={{ backgroundColor: color.hex }} />
                        <span className="font-bold text-xs uppercase tracking-wide">{color.label}</span>
                        {selected && <Check size={14} className="text-[#FF3B30]" />}
                      </button>
                    );
                  })}
                </div>
                <div className="flex justify-center gap-4 mt-8">
                  <button onClick={skipOrContinueMulti}
                    className="inline-flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#D62828] transition-colors"
                    data-testid="quiz-colors-continue">
                    {(answers.preferred_colors || []).length > 0 ? 'Continue' : 'Skip — No Preference'} <ArrowRight size={18} />
                  </button>
                </div>
              </>
            )}

            {/* Custom Size Step (Step 6) — with optional foot length + width measurement */}
            {current.isCustomSize && (
              <div className="mt-4">
                <div className="flex justify-center gap-3 mb-8">
                  <button onClick={() => setSizeMode('chart')}
                    className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wide border-2 transition-colors ${sizeMode === 'chart' ? 'border-[#0A0A0A] bg-[#0A0A0A] text-white' : 'border-[#E4E4E7] hover:border-[#0A0A0A]'}`}
                    data-testid="size-mode-chart">
                    I Know My Size
                  </button>
                  <button onClick={() => setSizeMode('measure')}
                    className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wide border-2 transition-colors flex items-center gap-2 ${sizeMode === 'measure' ? 'border-[#FF3B30] bg-[#FF3B30] text-white' : 'border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white'}`}
                    data-testid="size-mode-measure">
                    <Ruler size={16} /> Measure Your Feet
                  </button>
                </div>

                {sizeMode === 'chart' && (
                  <>
                    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-9 gap-3 mb-8">
                      {sizeChart.map(row => (
                        <button key={row.us} onClick={() => selectOption(row.us)}
                          data-testid={`quiz-option-${row.us}`}
                          className={`border-2 bg-white p-3 hover:border-[#0A0A0A] cursor-pointer transition-all flex flex-col items-center text-center
                            ${answers.size === row.us ? 'border-[#FF3B30] bg-[#F4F4F5]' : 'border-[#E4E4E7]'}`}>
                          <span className="font-bold text-sm">US {row.us}</span>
                        </button>
                      ))}
                    </div>
                    <div className="text-center mb-4">
                      <button onClick={() => setShowFullChart(!showFullChart)}
                        className="text-xs font-bold uppercase tracking-wide text-[#FF3B30] hover:text-[#D62828] transition-colors underline underline-offset-4"
                        data-testid="toggle-size-chart">
                        {showFullChart ? 'Hide' : 'View'} US / UK / EU Size Chart
                      </button>
                    </div>
                    {showFullChart && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="overflow-hidden">
                        <div className="border border-[#E4E4E7] overflow-x-auto" data-testid="size-chart-table">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-[#0A0A0A] text-white">
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide">US</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide">UK</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide">EU</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide">Foot (cm)</th>
                                <th className="px-4 py-3"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {sizeChart.map((row, i) => (
                                <tr key={row.us} className={`border-t border-[#E4E4E7] hover:bg-[#F4F4F5] cursor-pointer transition-colors ${i % 2 === 0 ? '' : 'bg-[#FAFAFA]'}`}
                                  onClick={() => selectOption(row.us)}>
                                  <td className="px-4 py-2.5 font-bold">US {row.us}</td>
                                  <td className="px-4 py-2.5">UK {row.uk}</td>
                                  <td className="px-4 py-2.5">EU {row.eu}</td>
                                  <td className="px-4 py-2.5">{row.cm} cm</td>
                                  <td className="px-4 py-2.5 text-right">
                                    <span className="text-xs font-bold uppercase text-[#FF3B30]">Select</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </motion.div>
                    )}
                  </>
                )}

                {sizeMode === 'measure' && (
                  <div className="max-w-lg mx-auto" data-testid="foot-measure-section">
                    <div className="border-2 border-[#E4E4E7] p-6 mb-6">
                      <h3 className="font-heading font-bold text-lg mb-4">How to Measure Your Feet</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wide text-[#FF3B30] mb-3 flex items-center gap-2">
                            <Ruler size={16} /> Foot Length
                          </p>
                          <ol className="space-y-2 text-sm text-[#52525B]">
                            <li className="flex gap-3"><span className="font-bold text-[#0A0A0A] shrink-0">1.</span>Place paper on the floor against a wall</li>
                            <li className="flex gap-3"><span className="font-bold text-[#0A0A0A] shrink-0">2.</span>Stand with your heel against the wall</li>
                            <li className="flex gap-3"><span className="font-bold text-[#0A0A0A] shrink-0">3.</span>Mark the tip of your longest toe</li>
                            <li className="flex gap-3"><span className="font-bold text-[#0A0A0A] shrink-0">4.</span>Measure wall to mark in cm</li>
                          </ol>
                        </div>
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wide text-[#FF3B30] mb-3 flex items-center gap-2">
                            <ArrowsHorizontal size={16} /> Foot Width
                          </p>
                          <ol className="space-y-2 text-sm text-[#52525B]">
                            <li className="flex gap-3"><span className="font-bold text-[#0A0A0A] shrink-0">1.</span>While standing, find the widest part of your foot (ball area)</li>
                            <li className="flex gap-3"><span className="font-bold text-[#0A0A0A] shrink-0">2.</span>Wrap a measuring tape around this point</li>
                            <li className="flex gap-3"><span className="font-bold text-[#0A0A0A] shrink-0">3.</span>Or mark both sides and measure the distance in cm</li>
                          </ol>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div>
                        <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] mb-2 block">Foot Length (cm)</label>
                        <input type="number" step="0.1" min="15" max="35" value={footCm}
                          onChange={(e) => setFootCm(e.target.value)} placeholder="e.g. 26.5"
                          className="w-full border-2 border-[#E4E4E7] px-4 py-4 text-lg font-bold text-center focus:border-[#FF3B30] focus:outline-none transition-colors"
                          data-testid="foot-cm-input" />
                      </div>
                      <div>
                        <label className="text-xs font-bold uppercase tracking-wide text-[#52525B] mb-2 block">Foot Width (cm)</label>
                        <input type="number" step="0.1" min="5" max="15" value={footWidthCm}
                          onChange={(e) => setFootWidthCm(e.target.value)} placeholder="e.g. 9.5"
                          className="w-full border-2 border-[#E4E4E7] px-4 py-4 text-lg font-bold text-center focus:border-[#FF3B30] focus:outline-none transition-colors"
                          data-testid="foot-width-cm-input" />
                      </div>
                    </div>

                    {measuredSize && (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="border-2 border-[#FF3B30] bg-red-50 p-5 mb-4" data-testid="measured-result">
                        <p className="text-xs font-bold uppercase tracking-wide text-[#FF3B30] mb-3">Your Recommended Size</p>
                        <div className="grid grid-cols-4 gap-3 text-center">
                          <div>
                            <p className="text-xs text-[#52525B] uppercase font-bold">US</p>
                            <p className="font-black text-2xl">{measuredSize.us}</p>
                          </div>
                          <div>
                            <p className="text-xs text-[#52525B] uppercase font-bold">UK</p>
                            <p className="font-black text-2xl">{measuredSize.uk}</p>
                          </div>
                          <div>
                            <p className="text-xs text-[#52525B] uppercase font-bold">EU</p>
                            <p className="font-black text-2xl">{measuredSize.eu}</p>
                          </div>
                          <div>
                            <p className="text-xs text-[#52525B] uppercase font-bold">CM</p>
                            <p className="font-black text-2xl">{measuredSize.cm}</p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {measuredWidth && (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="border-2 border-[#0A0A0A] bg-[#F4F4F5] p-5 mb-6" data-testid="measured-width-result">
                        <p className="text-xs font-bold uppercase tracking-wide text-[#0A0A0A] mb-2">Recommended Width Fitting</p>
                        <div className="flex items-center gap-3">
                          <span className="font-black text-3xl">{measuredWidth.label}</span>
                          <span className="text-sm text-[#52525B]">{measuredWidth.desc}</span>
                        </div>
                        <p className="text-xs text-[#52525B] mt-2">This will be pre-selected in the next step. You can change it if needed.</p>
                      </motion.div>
                    )}

                    <button onClick={confirmMeasuredSize} disabled={!measuredSize}
                      className={`w-full inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wide px-8 py-4 transition-colors ${measuredSize ? 'bg-[#FF3B30] text-white hover:bg-[#D62828]' : 'bg-[#E4E4E7] text-[#A1A1AA] cursor-not-allowed'}`}
                      data-testid="confirm-measured-size">
                      Confirm Size US {measuredSize?.us || '—'} {measuredWidth ? `/ Width ${measuredWidth.label}` : ''} <ArrowRight size={18} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Width Fitting Step (Step 7) — with AI recommendation */}
            {current.isWidthStep && (
              <div className="mt-4">
                {measuredWidth && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                    className="border-2 border-[#FF3B30] bg-red-50 p-5 mb-8 text-center max-w-md mx-auto" data-testid="width-ai-recommendation">
                    <p className="text-xs font-bold uppercase tracking-wide text-[#FF3B30] mb-2">Based on Your Foot Measurement</p>
                    <p className="font-black text-2xl mb-1">{measuredWidth.label}</p>
                    <p className="text-sm text-[#52525B]">{measuredWidth.desc}</p>
                    <button onClick={() => selectOption(measuredWidth.code)}
                      className="mt-4 inline-flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-8 py-3 hover:bg-[#D62828] transition-colors"
                      data-testid="accept-width-recommendation">
                      Use Recommended Width <ArrowRight size={18} />
                    </button>
                  </motion.div>
                )}

                <p className="text-center text-xs font-bold uppercase tracking-wide text-[#52525B] mb-4">
                  {measuredWidth ? 'Or choose a different width manually:' : 'Standard shoe width from narrow to extra wide:'}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 max-w-3xl mx-auto">
                  {widthOptions.map((opt) => (
                    <button key={opt.value} onClick={() => selectOption(opt.value)}
                      data-testid={`quiz-option-${opt.value}`}
                      data-state={answers.foot_width === opt.value ? 'selected' : undefined}
                      className={`border-2 bg-white hover:border-[#0A0A0A] cursor-pointer transition-all p-4 flex flex-col items-center gap-2 text-center
                        ${answers.foot_width === opt.value ? 'border-[#FF3B30] bg-[#F4F4F5]' : 'border-[#E4E4E7]'}
                        ${measuredWidth && measuredWidth.code === opt.value ? 'ring-2 ring-[#FF3B30] ring-offset-2' : ''}`}>
                      <span className="font-bold text-sm uppercase tracking-wide">{opt.label}</span>
                      <span className="text-xs text-[#52525B] normal-case">{opt.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Standard single-select options (Gender, Activity, Budget, Arch Type) */}
            {!current.multiSelect && !current.isCustomSize && !current.isWidthStep && currentOptions && (
              <>
                <div className={`grid gap-4 ${(currentOptions || []).length <= 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-3'}`}>
                  {(currentOptions || []).map((opt) => (
                    <button key={opt.value} onClick={() => selectOption(opt.value)}
                      data-testid={`quiz-option-${opt.value}`}
                      data-state={answers[current.key] === opt.value ? 'selected' : undefined}
                      className={`border-2 bg-white hover:border-[#0A0A0A] cursor-pointer transition-all flex flex-col items-center gap-2 text-center p-4 sm:p-6
                        ${answers[current.key] === opt.value ? 'border-[#FF3B30] bg-[#F4F4F5]' : 'border-[#E4E4E7]'}`}>
                      {opt.icon && <span className="text-[#FF3B30]">{opt.icon}</span>}
                      <span className="font-bold text-sm uppercase tracking-wide">{opt.label}</span>
                      {opt.desc && <span className="text-xs text-[#52525B] normal-case">{opt.desc}</span>}
                    </button>
                  ))}
                </div>
                {current.skippable && (
                  <div className="flex justify-center mt-8">
                    <button onClick={skipOrContinueMulti}
                      className="inline-flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#D62828] transition-colors"
                      data-testid="quiz-skip-btn">
                      Skip — No Preference <ArrowRight size={18} />
                    </button>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {step > 0 && (
          <button onClick={() => setStep(step - 1)} className="mt-8 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#52525B] hover:text-[#0A0A0A] transition-colors" data-testid="quiz-back-btn">
            <ArrowLeft size={16} /> Back
          </button>
        )}
      </div>
    </div>
  );
}
